import React from "react";
import { toast as shellToast } from "../../shell/toast";
import { I, Icon, Field, OpsToggleRow, BrandColorPicker, FontSelect } from "./jrn-ui";
import { journeyScreens, journeyServices, resolveTheme, tileImage, eventImage, TILE_PRESETS, baseURL, linesOfBusiness, TEXT_KEYS, journeyURL, type Journey, type Patch, applyScanToJourney } from "./journey-model";
import * as ApiClient from "@v0y4ge/api-client";
import { useTenantVocab } from "../../lib/tenant-vocab";
import { useT } from "@v0y4ge/i18n-runtime";
import { SegmentedControl, Btn, IconBtn, Input, Dropdown, Slider, Checkbox, T } from "@v0y4ge/jrni-ui";
import { NumberField } from "../../lib/NumberField";
type Toastish = { tone?: "success" | "danger" | "warn" | "info"; title: string; sub?: string };
const toast = (o: Toastish) => { try { shellToast({ tone: o.tone, title: o.title, subtitle: o.sub }); } catch { /* toast host absent in preview */ } };

/* Journey RECORDS stay the dynamic `Journey` alias (journey-model) — every panel
 * edits a different corner of that config. The panels' own contracts are typed. */
interface PanelProps { j: Journey; set: (patch: Patch) => void }
interface SharePanelProps extends PanelProps { onPreview: () => void }
interface OptionCard { id: string; label: string; desc?: string; visual?: React.ReactNode }
/** Result of `scanWebsite` — a loose brand bag until the scan op is real (REQ-063). */
type BrandScan = Record<string, any>;

// Journey editor — section panels. Each gets { j, set } where set() deep-merges a patch.

function JPane({ title, sub, children, right, hidden }: { title: React.ReactNode; sub?: React.ReactNode; children?: React.ReactNode; right?: React.ReactNode; hidden?: boolean }) {
  if (hidden) return null;
  return (
    <div className="card" style={{ overflow: "visible" }}>
      <div style={{ padding: "14px 18px 0", display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--jrni-color-text-strong)" }}>{title}</div>
          {sub && <div style={{ fontSize: 11.5, color: "var(--jrni-color-text-soft)", marginTop: 2, lineHeight: 1.5, textWrap: "pretty" }}>{sub}</div>}
        </div>
        {right}
      </div>
      <div style={{ padding: "14px 18px 18px", display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
    </div>
  );
}

function JOptionCards({ value, onChange, options, cols }: { value: string; onChange: (id: string) => void; options: OptionCard[]; cols?: number }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols || options.length}, minmax(0,1fr))`, gap: 8 }}>
      {options.map((o) => {
        const active = value === o.id;
        return (
          // ui-raw: bespoke selectable option card (icon/visual + label + description, active tint + border/margin states) — not a labeled Btn
          <button key={o.id} onClick={() => onChange(o.id)} style={{
            textAlign: "left", padding: "11px 12px", fontFamily: "inherit", cursor: "pointer",
            background: active ? "var(--jrni-color-primary-4)" : "var(--jrni-color-surface-card)",
            border: active ? "2px solid var(--jrni-color-primary-1)" : "1px solid var(--jrni-color-neutral-5)",
            borderRadius: "var(--jrni-radius-sm)", margin: active ? -1 : 0,
            display: "flex", flexDirection: "column", gap: 5,
          }}>
            {o.visual}
            <span style={{ fontSize: 12, fontWeight: 700, color: active ? "var(--jrni-color-primary-1)" : "var(--jrni-color-text-strong)" }}>{o.label}</span>
            {o.desc && <span style={{ fontSize: 10.5, color: "var(--jrni-color-text-soft)", lineHeight: 1.45 }}>{o.desc}</span>}
          </button>
        );
      })}
    </div>
  );
}

// mini wireframe visuals for flow/progress pickers
function JWire({ kind }: { kind: string }) {
  const b = "var(--jrni-color-primary-1)", n = "var(--jrni-color-neutral-4)";
  const bar = (w: number, c: string, h: number = 5) => <span style={{ display: "block", width: w, height: h, borderRadius: 2, background: c }} />;
  if (kind === "service-first") return <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>{bar(34, b)}{bar(46, n)}{bar(46, n)}{bar(28, n)}</span>;
  if (kind === "location-first") return <span style={{ display: "flex", flexDirection: "column", gap: 3 }}><span style={{ display: "flex", gap: 3 }}>{bar(14, b)}{bar(14, b)}</span>{bar(46, n)}{bar(46, n)}</span>;
  if (kind === "map-first") return <span style={{ display: "block", width: 46, height: 24, borderRadius: 3, background: `linear-gradient(135deg, ${"var(--jrni-color-primary-3)"}, var(--jrni-color-neutral-6))`, position: "relative" }}><span style={{ position: "absolute", left: 8, top: 6, width: 6, height: 6, borderRadius: "50%", background: b }} /><span style={{ position: "absolute", left: 26, top: 12, width: 6, height: 6, borderRadius: "50%", background: b }} /></span>;
  if (kind === "hub") return <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>{[b, n, n].map((c, i) => <span key={i} style={{ display: "flex", gap: 3, alignItems: "center" }}><span style={{ width: 10, height: 10, borderRadius: 3, background: c }} /><span style={{ width: 32, height: 6, borderRadius: 2, background: n }} /></span>)}</span>;
  if (kind === "event") return <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>{[b, n].map((c, i) => <span key={i} style={{ display: "flex", gap: 4, alignItems: "center" }}><span style={{ width: 13, height: 13, borderRadius: 3, background: c, flexShrink: 0 }} /><span style={{ display: "flex", flexDirection: "column", gap: 2 }}><span style={{ width: 30, height: 5, borderRadius: 2, background: n }} /><span style={{ width: 20, height: 3, borderRadius: 2, background: "var(--jrni-color-neutral-5)" }} /></span></span>)}</span>;
  if (kind === "chat") return <span style={{ display: "flex", flexDirection: "column", gap: 3 }}><span style={{ width: 34, height: 9, borderRadius: 5, background: n, alignSelf: "flex-start" }} /><span style={{ width: 26, height: 9, borderRadius: 5, background: b, alignSelf: "flex-end" }} /><span style={{ width: 38, height: 9, borderRadius: 5, background: n, alignSelf: "flex-start" }} /></span>;
  if (kind === "bar") return <span style={{ display: "block", width: 46, height: 4, borderRadius: 2, background: n }}><span style={{ display: "block", width: 24, height: 4, borderRadius: 2, background: b }} /></span>;
  if (kind === "steps") return <span style={{ display: "flex", gap: 3, alignItems: "center" }}>{[1, 2, 3].map((i) => <span key={i} style={{ width: 9, height: 9, borderRadius: "50%", background: i === 1 ? b : "transparent", border: `1.5px solid ${i === 1 ? b : n}` }} />)}</span>;
  if (kind === "tiles") return <span style={{ display: "flex", gap: 3 }}>{[14, 18, 14].map((w, i) => <span key={i} style={{ width: w, height: 9, borderRadius: 999, background: i === 0 ? b : "transparent", border: i === 0 ? 0 : `1px solid ${n}` }} />)}</span>;
  return null;
}

// ═══ Website brand scan — shared by Design panel + creator wizard ═══
// Calls the REAL settings-owned scanner op `scanBrandFromWebsite` (REQ-076) —
// the fabricated hash-derived local scanner is GONE (CC GM14 §3 item 1). Same
// guarded resolution + honest-failure contract as Settings → Branding: the op
// is implemented in api-client/src but not barrel-exported and has no mock
// handler yet, so the guard reports an honest error until the kit ships it.
// Only server-returned values are ever rendered — no invented provenance.
function BrandScanCard({ onApply, compact }: { onApply: (scan: BrandScan) => void; compact?: boolean }) {
  const t = useT();
  const [url, setUrl] = React.useState("");
  const [phase, setPhase] = React.useState("idle"); // idle | scanning | done | error
  const [scan, setScan] = React.useState<BrandScan | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const run = async () => {
    const raw = url.trim();
    if (!raw || phase === "scanning") return;
    const scanOp = (ApiClient as unknown as Record<string, unknown>).scanBrandFromWebsite as
      ((input: { url: string }, options: unknown) => Promise<{ draft?: Record<string, string> }>) | undefined;
    if (typeof scanOp !== "function") { setErr(t("journeys.panels.scanEndpointUnavailable", "The scan endpoint isn't available in this build (REQ-076) — nothing was imported.")); setPhase("error"); return; }
    setPhase("scanning"); setScan(null); setErr(null);
    try {
      const res = await scanOp({ url: raw }, {});
      const d = res?.draft ?? {};
      const next: BrandScan = { domain: d.domain || raw.replace(/^https?:\/\//, "").replace(/^www\./, "").split(/[/?#]/)[0] };
      if (d.name) next.name = d.name;
      if (d.primary) next.brandColor = d.primary;
      if (d.accent) next.accentColor = d.accent;
      if (d.cover) next.backgroundColor = d.cover;
      if (d.headingFont) next.headingFont = d.headingFont;
      if (d.bodyFont) next.bodyFont = d.bodyFont;
      if (Object.keys(next).length <= 1) { setErr(t("journeys.panels.scanReturnedNothingUsable", "The scan returned nothing usable for that address.")); setPhase("error"); return; }
      setScan(next); setPhase("done");
    } catch (e) {
      console.warn("scanBrandFromWebsite failed:", e);
      setErr(t("journeys.panels.couldntScanThatWebsite", "Couldn't scan that website — nothing was imported.")); setPhase("error");
    }
  };
  const sw = (c: string | undefined, label: string) => c ? (
    <span key={label} title={label} className="col" style={{ gap: 3, alignItems: "center" }}>
      <span style={{ width: 30, height: 30, borderRadius: 8, background: c, border: "1px solid rgba(20,22,30,0.12)" }} />
      <span className="mono" style={{ fontSize: 8.5, color: "var(--jrni-color-text-soft)" }}>{c.toUpperCase()}</span>
    </span>
  ) : null;
  return (
    <div className="col" style={{ gap: 10 }}>
      <div className="row" style={{ gap: 8 }}>
        <Input placeholder={t("journeys.panels.placeholder", "yourbrand.com")} value={url} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setUrl(e.target.value); if (phase === "error") setPhase("idle"); }} onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter") void run(); }} style={{ flex: 1, minWidth: 0 }} />
        <Btn size="sm" disabled={phase === "scanning" || !url.trim()} style={{ opacity: phase === "scanning" || !url.trim() ? 0.5 : 1 }} onClick={() => void run()}>{phase === "scanning" ? t("journeys.panels.scanning", "Scanning…") : t("journeys.panels.scan", "Scan")}</Btn>
      </div>
      {phase === "error" && <span className="field-help" style={{ color: T.red1 }}>{err}</span>}
      {phase === "scanning" && <span className="field-help">{t("journeys.panels.scanRunsOnTheServer", "Scanning — this runs on the server and imports only what it actually finds.")}</span>}
      {phase === "done" && scan && (
        <div className="col" style={{ gap: 10, padding: 12, background: "var(--jrni-color-surface-card-soft)", border: "1px solid var(--jrni-color-neutral-5)", borderRadius: "var(--jrni-radius-md)" }}>
          <div className="row" style={{ gap: 10, alignItems: "center" }}>
            <span style={{ width: 34, height: 34, borderRadius: 9, background: scan.brandColor || "var(--jrni-color-neutral-3)", color: T.onColor, display: "grid", placeItems: "center", fontWeight: 800, fontSize: 14 }}>{(scan.name || scan.domain).slice(0, 1).toUpperCase()}</span>
            <div className="col" style={{ gap: 1, minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--jrni-color-text-strong)" }}>{scan.name || scan.domain}</span>
              <span className="mono" style={{ fontSize: 10, color: "var(--jrni-color-text-soft)" }}>{scan.domain}</span>
            </div>
          </div>
          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            {sw(scan.brandColor, "Brand")}{sw(scan.accentColor, "Accent")}{sw(scan.backgroundColor, "Background")}{sw(scan.textColor, "Text")}
            {(scan.headingFont || scan.bodyFont) && <span className="col" style={{ gap: 3, justifyContent: "center", paddingLeft: 4 }}>
              {scan.headingFont && <span style={{ fontSize: 12, fontWeight: 700, color: "var(--jrni-color-text-strong)" }}>{scan.headingFont}</span>}
              {scan.bodyFont && <span style={{ fontSize: 10.5, color: "var(--jrni-color-text-soft)" }}>{scan.bodyFont}</span>}
            </span>}
          </div>
          {!compact && <span className="field-help">{t("journeys.panels.onlyValuesTheScanReturned", "Only values the scan actually returned are shown — apply sets them on this journey's theme.")}</span>}
          <Btn size="sm" style={{ alignSelf: "flex-start" }} onClick={() => { onApply(scan); toast({ title: t("journeys.panels.brandingApplied", "Branding applied"), sub: scan.name || scan.domain }); }}>{t("journeys.panels.applyToThisJourney", "Apply to this journey")}</Btn>
        </div>
      )}
    </div>
  );
}

// ═══ Design ═══
function JDesignPanel({ j, set }: PanelProps) {
  const t = useT();
  const th = j.theme;
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title6')} sub={t('journeys.panels.sub')}>
        <OpsToggleRow label={t('journeys.panels.label2')} sub={t('journeys.panels.sub2')} value={th.inheritBrand} onChange={(v) => set({ theme: { inheritBrand: v } })} />
      </JPane>
      <JPane title={t('journeys.panels.title7')} sub={t('journeys.panels.sub3')}>
        <BrandScanCard onApply={(scan) => set(applyScanToJourney(scan))} />
      </JPane>
      {!th.inheritBrand && (
        <JPane title={t('journeys.panels.title8')} sub={t('journeys.panels.sub4')}>
          <div className="row gap-3" style={{ flexWrap: "wrap" }}>
            <Field label={t('journeys.panels.label3')} flex><BrandColorPicker value={th.brandColor} onChange={(v) => set({ theme: { brandColor: v } })} /></Field>
            <Field label={t('journeys.panels.label4')} flex><BrandColorPicker value={th.accentColor} onChange={(v) => set({ theme: { accentColor: v } })} /></Field>
          </div>
          <div className="row gap-3" style={{ flexWrap: "wrap" }}>
            <Field label={t('journeys.panels.label5')} flex><BrandColorPicker value={th.backgroundColor} onChange={(v) => set({ theme: { backgroundColor: v } })} swatches={["#FFFFFF", "#FAFAF7", "#F4F1EA", "#F1F0F4", "#0F0F12", "#14161E"]} /></Field>
            <Field label={t('journeys.panels.label6')} flex><BrandColorPicker value={th.textColor} onChange={(v) => set({ theme: { textColor: v } })} swatches={["#14161E", "#2C2E38", "#3F4250", "#FFFFFF", "#EFEFEF"]} /></Field>
          </div>
          <div className="row gap-3">
            <Field label={t('journeys.panels.label7')} flex><FontSelect value={th.headingFont} onChange={(v) => set({ theme: { headingFont: v } })} /></Field>
            <Field label={t('journeys.panels.label8')} flex><FontSelect value={th.bodyFont} onChange={(v) => set({ theme: { bodyFont: v } })} /></Field>
          </div>
          <Field label={t('journeys.panels.label9')}>
            <SegmentedControl value={th.buttonShape} onChange={(v) => set({ theme: { buttonShape: v } })} options={[{ value: "sharp", label: "Sharp" }, { value: "rounded", label: "Rounded" }, { value: "pill", label: "Pill" }]} />
          </Field>
        </JPane>
      )}
      <JPane title={t('journeys.panels.title9')} sub={t('journeys.panels.sub5')}>
        <JOptionCards value={th.progress} onChange={(v) => set({ theme: { progress: v } })} options={[
          { id: "bar", label: "Thin bar", desc: "Minimal — a fill line along the top.", visual: <JWire kind="bar" /> },
          { id: "steps", label: "Numbered steps", desc: "Step dots with labels.", visual: <JWire kind="steps" /> },
          { id: "tiles", label: "Tiles", desc: "Labeled chips — completed ones are tappable.", visual: <JWire kind="tiles" /> },
        ]} />
      </JPane>
    </React.Fragment>
  );
}

// ═══ Header & footer ═══
function JHeaderFooterPanel({ j, set }: PanelProps) {
  const t = useT();
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title10')} sub={t('journeys.panels.sub6')}>
        <Field label={t('journeys.panels.label10')}>
          <SegmentedControl value={j.header.style} onChange={(v) => set({ header: { style: v } })} options={[{ value: "standard", label: "Standard" }, { value: "hero", label: "Hero welcome" }, { value: "compact", label: "Compact" }]} />
        </Field>
        {j.header.style === "hero" && <span className="field-help" style={{ marginTop: -8 }}>{t('journeys.panels.text14')}</span>}
        <div className="row gap-3" style={{ flexWrap: "wrap" }}>
          <Field label={t('journeys.panels.label11')} flex><Input value={j.header.tagline} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ header: { tagline: e.target.value } })} /></Field>
          <Field label={t('journeys.panels.label12')} flex><Input value={j.header.phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ header: { phone: e.target.value } })} /></Field>
        </div>
        <div className="row gap-4" style={{ flexWrap: "wrap" }}>
          <OpsToggleRow label={t('journeys.panels.label13')} value={j.header.showLogo} onChange={(v) => set({ header: { showLogo: v } })} />
          <OpsToggleRow label={t('journeys.panels.label14')} value={j.header.showPhone} onChange={(v) => set({ header: { showPhone: v } })} />
        </div>
      </JPane>
      <JPane title={t('journeys.panels.title11')} sub={t('journeys.panels.sub7')}>
        <OpsToggleRow label={t('journeys.panels.label15')} value={j.footer.show} onChange={(v) => set({ footer: { show: v } })} />
        {j.footer.show && (
          <React.Fragment>
            <OpsToggleRow label={t('journeys.panels.label16')} value={j.footer.showContact} onChange={(v) => set({ footer: { showContact: v } })} />
            {j.footer.showContact && <Field label={t('journeys.panels.label17')}><textarea className="textarea" rows={2} value={j.footer.contactCopy} onChange={(e) => set({ footer: { contactCopy: e.target.value } })} /></Field>}
            <OpsToggleRow label={t('journeys.panels.label18')} sub={t('journeys.panels.sub8')} value={j.footer.showPolicyLinks} onChange={(v) => set({ footer: { showPolicyLinks: v } })} />
            <OpsToggleRow label={t('journeys.panels.label19')} value={j.footer.showLegal} onChange={(v) => set({ footer: { showLegal: v } })} />
            {j.footer.showLegal && <Field label={t('journeys.panels.label20')}><Input value={j.footer.legalCopy} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ footer: { legalCopy: e.target.value } })} /></Field>}
          </React.Fragment>
        )}
      </JPane>
    </React.Fragment>
  );
}

// ═══ Flow ═══
function JFlowPanel({ j, set }: PanelProps) {
  const t = useT();
  const screens = journeyScreens(j);
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title12')} sub={t('journeys.panels.sub9')}>
        <JOptionCards value={j.flowOrder} onChange={(v) => set({ flowOrder: v, steps: v === "map-first" ? { location: { display: "map" } } : {} })} options={[
          { id: "service-first", label: "Service first", desc: "Pick a service, then where. Best when services differ a lot.", visual: <JWire kind="service-first" /> },
          { id: "location-first", label: "Location first", desc: "Pick a branch, then its services. Best for multi-site brands.", visual: <JWire kind="location-first" /> },
          { id: "map-first", label: "Map view", desc: "Open on a map with a search radius. Best for walk-in style networks.", visual: <JWire kind="map-first" /> },
        ]} />
      </JPane>
      <JPane title={t('journeys.panels.title13')} sub={t('journeys.panels.sub10')}>
        <div className="row" style={{ gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {screens.map((s, i) => (
            <React.Fragment key={s}>
              <span className="badge badge-primary" style={{ fontSize: 11 }}>{i + 1} · {({ service: "Services", location: "Location", staff: "Staff pick", time: "Time", details: "Details & consent", confirm: "Confirmation", join: "Join details", ticket: "Live ticket" })[s]}</span>
              {i < screens.length - 1 && <I.ChevronR size={10} style={{ color: "var(--jrni-color-text-soft)" }} />}
            </React.Fragment>
          ))}
        </div>
      </JPane>
      <JPane title={t('journeys.panels.title14')}>
        <OpsToggleRow label={t('journeys.panels.label21')} sub={t('journeys.panels.sub11')} value={j.allowBack} onChange={(v) => set({ allowBack: v })} />
        <OpsToggleRow label={t('journeys.panels.label22')} sub={t('journeys.panels.sub12')} value={j.autoAdvance} onChange={(v) => set({ autoAdvance: v })} />
      </JPane>
    </React.Fragment>
  );
}

// ═══ Services step ═══
function JServicesPanel({ j, set }: PanelProps) {
  const t = useT();
  const s = j.steps.service;
  useTenantVocab(); // §1.2.1 — subscribe: re-render when the served lob catalog resolves
  const lobsList = linesOfBusiness(); // served tenant taxonomy (REQ-091); empty until resolved
  const TPL = { linesOfBusiness: lobsList, lobById: Object.fromEntries(lobsList.map((x) => [x.id, x])) };
  const allLobs = TPL ? TPL.linesOfBusiness || [] : [];
  const toggleLob = (id: string) => {
    const has = s.lobs.includes(id);
    if (has && s.lobs.length === 1) { toast({ title: "Keep at least one line of business" }); return; }
    set({ steps: { service: { lobs: has ? s.lobs.filter((x: string) => x !== id) : [...s.lobs, id] } } });
  };
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title15')} sub={t('journeys.panels.sub13')}>
        <JOptionCards value={s.display} onChange={(v) => set({ steps: { service: { display: v } } })} options={[
          { id: "list", label: "List", desc: "Rows with icon, duration and description. Scans fastest." },
          { id: "tiles", label: "Tiles", desc: "Card grid — visual, best for few, distinct services." },
          { id: "showcase", label: "Showcase", desc: "Large image-led cards — hover reveals the description; quick book or open a full service page." },
        ]} />
        <div className="row gap-4" style={{ flexWrap: "wrap" }}>
          <OpsToggleRow label={t('journeys.panels.label23')} value={s.showDuration} onChange={(v) => set({ steps: { service: { showDuration: v } } })} />
          <OpsToggleRow label={t('journeys.panels.label24')} value={s.showDescription} onChange={(v) => set({ steps: { service: { showDescription: v } } })} />
        </div>
        <Field label={t('journeys.panels.label25')}>
          <Dropdown value={s.groupBy} onChange={(v) => set({ steps: { service: { groupBy: v } } })} style={{ maxWidth: 240 }} options={[
            { value: "line-of-business", label: t('journeys.panels.text15') },
            { value: "popularity", label: t('journeys.panels.text16') },
          ]} />
        </Field>
      </JPane>
      <JPane title={t('journeys.panels.title16')} sub={t('journeys.panels.sub14')} hidden={j.type === "queue"}>
        <OpsToggleRow label={t('journeys.panels.label26')} sub={t('journeys.panels.sub15')} value={s.allowMultiple} onChange={(v) => set({ steps: { service: { allowMultiple: v } } })} />
        {s.allowMultiple && (
          <Field label={t('journeys.panels.label27')}>
            <NumberField min={2} value={s.maxServices} onChange={(n) => set({ steps: { service: { maxServices: Math.max(2, Math.min(5, n || 2)) } } })} style={{ width: 90 }} />
          </Field>
        )}
      </JPane>
      <JServiceImagesPane j={j} set={set} />
      {allLobs.length > 0 && !(j.custom && j.custom.services) && (
        <JPane title={t('journeys.panels.title17')} sub={t('journeys.panels.sub16')}>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {allLobs.map((l) => (
              // ui-raw: bespoke toggleable line-of-business chip (.chip active CSS + inline color dot); Chip primitive is a non-interactive display tag
              <button key={l.id} onClick={() => toggleLob(l.id)} className={"chip " + (s.lobs.includes(l.id) ? "active" : "")} style={{ height: 28, fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ width: 8, height: 8, borderRadius: 3, background: l.color }} />{l.name}
              </button>
            ))}
          </div>
        </JPane>
      )}
      {j.custom && j.custom.services && (
        <JPane title={t('journeys.panels.title18')} sub={t('journeys.panels.sub17')}>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {j.custom.services.map((s: any) => <span key={s.id} className="badge badge-soft">{s.name} · {s.duration} {t('journeys.panels.text17')}</span>)}
          </div>
        </JPane>
      )}
    </React.Fragment>
  );
}

// ═══ Service images pane ═══
function JServiceImagesPane({ j, set }: PanelProps) {
  const t = useT();
  const svcs = journeyServices(j);
  const th = resolveTheme(j);
  const imgs = j.steps.service.images || {};
  const fileRef = React.useRef<HTMLInputElement | null>(null);
  const pendingId = React.useRef<string | null>(null);
  const setImg = (svcId: string, val: any) => set({ steps: { service: { images: { [svcId]: val } } } });
  const pickFile = (svcId: string) => { pendingId.current = svcId; fileRef.current && fileRef.current.click(); };
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    const svcId = pendingId.current;
    if (!f || !svcId) return;
    const r = new FileReader();
    r.onload = () => {
      const im = new Image();
      im.onload = () => {
        const w = 480, h = Math.round((im.height / im.width) * 480) || 300;
        const cv = document.createElement("canvas"); cv.width = w; cv.height = h;
        cv.getContext("2d")?.drawImage(im, 0, 0, w, h);
        setImg(svcId, { kind: "upload", src: cv.toDataURL("image/jpeg", 0.78) });
        toast({ title: "Image added", sub: f.name + " → resized for tiles" });
      };
      im.src = String(r.result || "");
    };
    r.readAsDataURL(f);
  };
  const previewStyle = (svcId: string) => tileImage({ ...j, steps: { ...j.steps, service: { ...j.steps.service, showImages: true } } }, th, svcId);
  return (
    <JPane title={t('journeys.panels.title19')} sub={t('journeys.panels.sub18')}>
      {/* ui-raw: hidden native file picker triggered via ref; Input models no file type and would render visible chrome */}
      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} />
      <OpsToggleRow label={t('journeys.panels.label28')} sub={t('journeys.panels.sub19')} value={j.steps.service.showImages !== false} onChange={(v) => set({ steps: { service: { showImages: v } } })} />
      <div className="col" style={{ gap: 8 }}>
        {svcs.map((s) => {
          const cur = imgs[s.id];
          const pv = previewStyle(s.id);
          return (
            <div key={s.id} className="row" style={{ gap: 10, alignItems: "center", padding: "8px 10px", background: "var(--jrni-color-surface-card-soft)", border: "1px solid var(--jrni-color-neutral-5)", borderRadius: "var(--jrni-radius-sm)", flexWrap: "wrap" }}>
              <span style={{ width: 56, height: 38, borderRadius: 7, flexShrink: 0, border: "1px solid var(--jrni-color-neutral-5)", ...(pv || { background: "var(--jrni-color-neutral-6)" }), display: "grid", placeItems: "center" }}>{!pv && <Icon name="tags" size={13} style={{ color: "var(--jrni-color-text-soft)" }} />}</span>
              <span style={{ flex: 1, minWidth: 120, fontSize: 12.5, fontWeight: 600, color: "var(--jrni-color-text-strong)" }}>{s.name}</span>
              <span className="row" style={{ gap: 5, alignItems: "center" }}>
                {TILE_PRESETS.map((p) => {
                  const active = cur && cur.kind === "preset" && cur.id === p.id;
                  // ui-raw: 22px color-preset swatch button (gradient/brand fill, active ring) — a visual picker cell, not a labeled Btn
                  return <button key={p.id} title={t(p.labelKey, p.label)} onClick={() => setImg(s.id, { kind: "preset", id: p.id })} style={{ width: 22, height: 22, borderRadius: 6, cursor: "pointer", padding: 0, background: p.css || `linear-gradient(135deg, ${th.brandColor}, ${th.accentColor})`, border: active ? "2px solid var(--jrni-color-primary-1)" : "1px solid var(--jrni-color-neutral-4)", boxShadow: active ? "0 0 0 2px var(--jrni-color-primary-3)" : "none" }} />;
                })}
                <Btn size="sm" variant="secondary" onClick={() => pickFile(s.id)} title={t('journeys.panels.title20')} icon={<Icon name="edit" size={12} />}>{cur && cur.kind === "upload" ? t('journeys.panels.text18') : t('journeys.panels.text19')}</Btn>
                {cur && <Btn size="sm" variant="ghost" title={t('journeys.panels.title21')} onClick={() => setImg(s.id, undefined)} iconOnly={<I.X size={11} />} />}
              </span>
            </div>
          );
        })}
      </div>
      <span className="field-help">{t('journeys.panels.text20')}</span>
    </JPane>
  );
}

// ═══ Locations step ═══
function JLocationsPanel({ j, set }: PanelProps) {
  const t = useT();
  const s = j.steps.location;
  const allLocs = (j.custom && j.custom.locations) || (j.custom && j.custom.locations || []);
  const inR = allLocs.filter((l: any) => !s.radiusEnabled || l.distanceMi <= s.radiusMi);
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title15')} sub={t('journeys.panels.sub20')}>
        <JOptionCards value={s.display} onChange={(v) => set({ steps: { location: { display: v } } })} options={[
          { id: "list", label: "List only", desc: "Compact cards, no map." },
          { id: "map-list", label: "Map + list", desc: "Pin map above the branch cards." },
          { id: "map", label: "Map view", desc: "Full map — tap a pin for details." },
        ]} />
        <div className="row gap-4" style={{ flexWrap: "wrap" }}>
          <OpsToggleRow label={t('journeys.panels.label29')} value={s.showHours} onChange={(v) => set({ steps: { location: { showHours: v } } })} />
          <OpsToggleRow label={t('journeys.panels.label30')} value={s.showServices} onChange={(v) => set({ steps: { location: { showServices: v } } })} />
          <OpsToggleRow label={t('journeys.panels.label31')} value={s.showDistance} onChange={(v) => set({ steps: { location: { showDistance: v } } })} />
        </div>
      </JPane>
      <JPane title={t('journeys.panels.title22')} sub={t('journeys.panels.sub21')}>
        <OpsToggleRow label={t('journeys.panels.label32')} value={s.radiusEnabled} onChange={(v) => set({ steps: { location: { radiusEnabled: v } } })} />
        {s.radiusEnabled && (
          <div className="col" style={{ gap: 8 }}>
            <div className="row" style={{ gap: 10, alignItems: "center" }}>
              <Slider min={5} max={50} step={5} value={s.radiusMi} onChange={(v) => set({ steps: { location: { radiusMi: v } } })} style={{ flex: 1 }} />
              <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: "var(--jrni-color-text-strong)", width: 52 }}>{s.radiusMi} {t('journeys.panels.text21')}</span>
            </div>
            <span className="field-help">{inR.length} {t('journeys.panels.text22')}{" "}{allLocs.length} {t('journeys.panels.text23')}</span>
          </div>
        )}
      </JPane>
      <JPane title={t('journeys.panels.title23')} sub={t('journeys.panels.sub22')}>
        <OpsToggleRow label={t('journeys.panels.label33')} sub={t('journeys.panels.sub23')} value={s.showVideoOption} onChange={(v) => set({ steps: { location: { showVideoOption: v } } })} />
        <OpsToggleRow label={t('journeys.panels.label34')} sub={t('journeys.panels.sub24')} value={s.showPhoneOption} onChange={(v) => set({ steps: { location: { showPhoneOption: v } } })} />
      </JPane>
    </React.Fragment>
  );
}

// ═══ Staff step ═══
function JStaffPanel({ j, set }: PanelProps) {
  const t = useT();
  const s = j.steps.staff;
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title24')} sub={t('journeys.panels.sub25')}>
        <JOptionCards value={s.enabled ? s.choice : "off"} cols={3} onChange={(v) => set({ steps: { staff: v === "off" ? { enabled: false } : { enabled: true, choice: v } } })} options={[
          { id: "optional", label: "Optional pick", desc: "'First available' is the default; picking a person is a choice." },
          { id: "required", label: "Must pick", desc: "Customers always choose a specialist. Best for relationship-led services." },
          { id: "off", label: "Hidden", desc: "No staff step — assignment happens on your side." },
        ]} />
      </JPane>
      {s.enabled && (
        <JPane title={t('journeys.panels.title25')} sub={t('journeys.panels.sub26')}>
          <div className="row gap-4" style={{ flexWrap: "wrap" }}>
            <OpsToggleRow label={t('journeys.panels.label35')} value={s.showRole} onChange={(v) => set({ steps: { staff: { showRole: v } } })} />
            <OpsToggleRow label={t('journeys.panels.label36')} value={s.showRating} onChange={(v) => set({ steps: { staff: { showRating: v } } })} />
            <OpsToggleRow label={t('journeys.panels.label37')} value={s.showNextAvail} onChange={(v) => set({ steps: { staff: { showNextAvail: v } } })} />
            <OpsToggleRow label={t('journeys.panels.label38')} value={s.showBio} onChange={(v) => set({ steps: { staff: { showBio: v } } })} />
          </div>
        </JPane>
      )}
    </React.Fragment>
  );
}

// ═══ Time step ═══
function JTimePanel({ j, set }: PanelProps) {
  const t = useT();
  const s = j.steps.time;
  const multiOn = j.steps.service.allowMultiple;
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title26')}>
        <Field label={t('journeys.panels.label39')}>
          <Dropdown value={s.window} onChange={(v) => set({ steps: { time: { window: v } } })} style={{ maxWidth: 240 }} options={[
            { value: "next-7-days", label: t('journeys.panels.text24') },
            { value: "next-14-days", label: t('journeys.panels.text25') },
            { value: "next-30-days", label: t('journeys.panels.text26') },
          ]} />
        </Field>
        <OpsToggleRow label={t('journeys.panels.label40')} sub={t('journeys.panels.sub27')} value={s.showOnlyAvailable} onChange={(v) => set({ steps: { time: { showOnlyAvailable: v } } })} />
      </JPane>
      <JPane title={t('journeys.panels.title27')} sub={t('journeys.panels.sub28')}>
        <OpsToggleRow label={t('journeys.panels.label41')} sub={t('journeys.panels.sub29')} value={s.waitlist !== false} onChange={(v) => set({ steps: { time: { waitlist: v } } })} />
      </JPane>
      <JPane title={t('journeys.panels.title28')} sub={multiOn ? "How times are matched when a customer books more than one service." : "Enable multi-service booking (Services section) to configure this."}>
        {multiOn ? (
          <React.Fragment>
            <JOptionCards value={s.multiStrategy} cols={3} onChange={(v) => set({ steps: { time: { multiStrategy: v } } })} options={[
              { id: "prefer-same", label: "Same specialist", desc: "Only offer times where one person can take every service back-to-back." },
              { id: "back-to-back", label: "Back-to-back", desc: "Chain services consecutively — hand over between specialists if needed." },
              { id: "independent", label: "Independent times", desc: "Each service gets its own time pick — visits can be separate." },
            ]} />
            {s.multiStrategy !== "independent" && (
              <Field label={t('journeys.panels.label42')}>
                <div className="row" style={{ gap: 10, alignItems: "center" }}>
                  <Slider min={0} max={30} step={5} value={s.bufferMin} onChange={(v) => set({ steps: { time: { bufferMin: v } } })} style={{ width: 180 }} />
                  <span className="mono" style={{ fontSize: 13, fontWeight: 700 }}>{s.bufferMin} {t('journeys.panels.text17')}</span>
                </div>
                <span className="field-help">{t('journeys.panels.text27')}</span>
              </Field>
            )}
            <div style={{ padding: "10px 12px", borderRadius: "var(--jrni-radius-sm)", background: "var(--jrni-color-primary-4)", border: "1px solid var(--jrni-color-primary-3)", fontSize: 11.5, color: "var(--jrni-color-text-body)", lineHeight: 1.55, display: "flex", gap: 8 }}>
              <I.Sparkle size={13} style={{ color: "var(--jrni-color-primary-1)", flexShrink: 0, marginTop: 1 }} />
              <span>{t('journeys.panels.text28')}</span>
            </div>
          </React.Fragment>
        ) : (
          <Btn size="sm" variant="secondary" style={{ alignSelf: "flex-start" }} onClick={() => set({ steps: { service: { allowMultiple: true } } })} icon={<I.Plus size={12} />}>{t('journeys.panels.text29')}</Btn>
        )}
      </JPane>
    </React.Fragment>
  );
}

// ═══ Details / fields / consent ═══
function JDetailsPanel({ j, set }: PanelProps) {
  const t = useT();
  const d = j.steps.details;
  const setFields = (fields: any[]) => set({ steps: { details: { fields } } });
  const upField = (i: number, patch: Record<string, unknown>) => setFields(d.fields.map((f: any, fi: number) => fi === i ? { ...f, ...patch } : f));
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title29')} sub={t('journeys.panels.sub30')}
             right={<Btn size="sm" variant="secondary" onClick={() => setFields([...d.fields, { id: "f" + Date.now(), label: t("journeys.panels.newQuestion", "New question"), type: "text", required: false, locked: false }])} icon={<I.Plus size={12} />}>{t('journeys.panels.text30')}</Btn>}>
        <div className="col" style={{ gap: 8 }}>
          {d.fields.map((f: any, i: number) => (
            <div key={f.id} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 104px 88px 28px", gap: 8, alignItems: "center", padding: "8px 10px", background: "var(--jrni-color-surface-card-soft)", border: "1px solid var(--jrni-color-neutral-5)", borderRadius: "var(--jrni-radius-sm)" }}>
              <div className="row" style={{ gap: 7, alignItems: "center", minWidth: 0 }}>
                {f.locked && <span title={t('journeys.panels.title30')}><Icon name="user" size={12} style={{ color: "var(--jrni-color-text-soft)", flexShrink: 0 }} /></span>}
                <Input size="sm" value={f.label} onChange={(e: React.ChangeEvent<HTMLInputElement>) => upField(i, { label: e.target.value })} />
              </div>
              <Dropdown disabled={f.locked} value={f.type} onChange={(v: string) => upField(i, { type: v })} options={["text", "email", "tel", "longtext", "date", "yesno"].map((ty) => ({ value: ty, label: ty }))} />
              <label className="row" style={{ gap: 6, fontSize: 11, color: "var(--jrni-color-text-body)", cursor: f.locked ? "not-allowed" : "pointer", opacity: f.locked ? 0.6 : 1 }}>
                <Checkbox disabled={f.locked} checked={f.required} onChange={(v: boolean) => upField(i, { required: v })} /> {t('journeys.panels.text31')}</label>
              {f.locked ? <span /> : <Btn variant="ghost" onClick={() => setFields(d.fields.filter((_: any, fi: number) => fi !== i))} title={t('journeys.panels.title31')} style={{ width: 26, height: 26, padding: 0, border: "1px solid var(--jrni-color-neutral-5)", background: T.white, borderRadius: 4, color: T.red1 }} iconOnly={<I.X size={11} />} />}
            </div>
          ))}
        </div>
      </JPane>
      <JPane title={t('journeys.panels.title32')} sub={t('journeys.panels.sub31')}>
        <OpsToggleRow label={t('journeys.panels.label43')} value={d.attendees.enabled} onChange={(v) => set({ steps: { details: { attendees: { enabled: v } } } })} />
        {d.attendees.enabled && (
          <div className="row gap-4" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
            <Field label={t('journeys.panels.label44')}>
              <NumberField min={1} value={d.attendees.max} onChange={(n) => set({ steps: { details: { attendees: { max: Math.max(1, Math.min(6, n || 1)) } } } })} style={{ width: 90 }} />
            </Field>
            <OpsToggleRow label={t('journeys.panels.label45')} sub={t('journeys.panels.sub32')} value={d.attendees.collectNames} onChange={(v) => set({ steps: { details: { attendees: { collectNames: v } } } })} />
          </div>
        )}
      </JPane>
      <JPane title={t('journeys.panels.title33')} sub={t('journeys.panels.sub33')}>
        <OpsToggleRow label={t('journeys.panels.label46')} sub={t('journeys.panels.sub34')} value={d.consent.required} onChange={(v) => set({ steps: { details: { consent: { required: v } } } })} />
        <Field label={t('journeys.panels.label47')}>
          <Input value={d.consent.policyUrl} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ steps: { details: { consent: { policyUrl: e.target.value } } } })} />
        </Field>
        <OpsToggleRow label={t('journeys.panels.label48')} sub={t('journeys.panels.sub35')} value={d.consent.marketingOptIn} onChange={(v) => set({ steps: { details: { consent: { marketingOptIn: v } } } })} />
        {d.consent.marketingOptIn && <Field label={t('journeys.panels.label49')}><Input value={d.consent.marketingLabel} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ steps: { details: { consent: { marketingLabel: e.target.value } } } })} /></Field>}
        {/* WhatsApp is opt-IN (CD-Docket 2026-08-04): the checkbox is the only way a
          * customer can ever consent — without it every WhatsApp send is suppressed
          * with consent_missing. Never pre-selected in the widget. A journey that
          * predates the field (no value stored) SHOWS the box — an unticked opt-in is
          * never harmful, and hiding it would silently keep the channel unusable;
          * only an explicit false turns it off. */}
        <OpsToggleRow
          label={t('journeys.panels.whatsappOptIn', 'Request WhatsApp opt-in')}
          sub={t('journeys.panels.whatsappOptInSub', 'Shows an unticked WhatsApp consent box. WhatsApp can only be used for customers who tick it.')}
          value={d.consent.whatsappOptIn !== false}
          onChange={(v) => set({ steps: { details: { consent: { whatsappOptIn: v } } } })} />
        {d.consent.whatsappOptIn !== false && <Field label={t('journeys.panels.whatsappOptInLabel', 'WhatsApp opt-in wording')}><Input value={d.consent.whatsappLabel || ""} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ steps: { details: { consent: { whatsappLabel: e.target.value } } } })} /></Field>}
      </JPane>
    </React.Fragment>
  );
}

// ═══ Confirmation ═══
function JConfirmPanel({ j, set }: PanelProps) {
  const t = useT();
  const c = j.steps.confirm;
  const T = (label: string, sub: string, key: string) => <OpsToggleRow label={label} sub={sub} value={(c as Record<string, any>)[key]} onChange={(v: boolean) => set({ steps: { confirm: { [key]: v } } })} />;
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title34')} sub={t('journeys.panels.sub36')}>
        {T("Appointment summary card", "Service, specialist, location, time and reference number.", "showSummary")}
        {T("Add to calendar", "Downloads an .ics — Google, Outlook, Apple.", "showAddToCalendar")}
        {T("Directions", "Opens the branch address in maps. Hidden for video appointments.", "showDirections")}
        {T("Reschedule", "Jumps back to the time step with the current slot held.", "showReschedule")}
        {T("Cancel appointment", "Two-tap cancel with your cancellation policy shown.", "showCancel")}
      </JPane>
      <JPane title={t('journeys.panels.title35')} sub={t('journeys.panels.sub37')}>
        <textarea className="textarea" rows={2} placeholder={t('journeys.panels.placeholder2')} value={c.note} onChange={(e) => set({ steps: { confirm: { note: e.target.value } } })} />
      </JPane>
    </React.Fragment>
  );
}

// ═══ Queue & wait (queue journeys) ═══
function JQueuePanel({ j, set }: PanelProps) {
  const t = useT();
  const q = j.steps.queue;
  const loc = j.steps.location;
  return (
    <React.Fragment>
      {j.type === "hub" && (
        <JPane title={t('journeys.panels.title36')} sub={t('journeys.panels.sub38')}>
          <OpsToggleRow label={t('journeys.panels.label50')} sub={t('journeys.panels.sub39')} value={q.chooseService !== false} onChange={(v) => set({ steps: { queue: { chooseService: v } } })} />
        </JPane>
      )}
      <JPane title={t('journeys.panels.title37')} sub={t('journeys.panels.sub40')}>
        <JOptionCards value={q.waitDisplay} cols={3} onChange={(v) => set({ steps: { queue: { waitDisplay: v } } })} options={[
          { id: "people", label: "People", desc: "‘4 waiting’ — simple, no promises." },
          { id: "time", label: "Minutes", desc: "‘~18 min’ — clearest for planning." },
          { id: "both", label: "Both", desc: "‘4 waiting · ~18 min’ — the default." },
        ]} />
        <OpsToggleRow label={t('journeys.panels.label51')} value={loc.showWait !== false} onChange={(v) => set({ steps: { location: { showWait: v } } })} />
      </JPane>
      <JPane title={t('journeys.panels.title38')} sub={t('journeys.panels.sub41')}>
        <OpsToggleRow label={t('journeys.panels.label52')} sub={t('journeys.panels.sub42')} value={loc.pauseEnabled} onChange={(v) => set({ steps: { location: { pauseEnabled: v } } })} />
        {loc.pauseEnabled && (
          <div className="row" style={{ gap: 10, alignItems: "center" }}>
            <Slider min={15} max={120} step={15} value={loc.pauseOverMin} onChange={(v) => set({ steps: { location: { pauseOverMin: v } } })} style={{ flex: 1, maxWidth: 220 }} />
            <span className="mono" style={{ fontSize: 13, fontWeight: 700 }}>{loc.pauseOverMin} {t('journeys.panels.text17')}</span>
            <span className="field-help" style={{ margin: 0 }}>{(j.custom && j.custom.locations || []).filter((l: any) => l.queue && l.queue.avgWait > loc.pauseOverMin).length} {t('journeys.panels.text22')}{(j.custom && j.custom.locations || []).length} {t('journeys.panels.text32')}</span>
          </div>
        )}
        <OpsToggleRow label={t('journeys.panels.label53')} sub={t('journeys.panels.sub43')} value={q.singleEntry} onChange={(v) => set({ steps: { queue: { singleEntry: v } } })} />
      </JPane>
      <JPane title={t('journeys.panels.title39')}>
        <OpsToggleRow label={t('journeys.panels.label54')} sub={t('journeys.panels.sub44')} value={q.smsUpdates} onChange={(v) => set({ steps: { queue: { smsUpdates: v } } })} />
        <Field label={t('journeys.panels.label55')}>
          <div className="row" style={{ gap: 10, alignItems: "center" }}>
            <Slider min={0} max={20} step={5} value={q.holdMin} onChange={(v) => set({ steps: { queue: { holdMin: v } } })} style={{ width: 180 }} />
            <span className="mono" style={{ fontSize: 13, fontWeight: 700 }}>{q.holdMin ? q.holdMin + " min" : t('journeys.panels.text33')}</span>
          </div>
          <span className="field-help">{t('journeys.panels.text34')}</span>
        </Field>
      </JPane>
    </React.Fragment>
  );
}

// ═══ Ticket (queue journeys) ═══
function JTicketPanel({ j, set }: PanelProps) {
  const t = useT();
  const c = j.steps.ticket;
  const T = (label: string, sub: string, key: string) => <OpsToggleRow label={label} sub={sub} value={(c as Record<string, any>)[key]} onChange={(v: boolean) => set({ steps: { ticket: { [key]: v } } })} />;
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title40')} sub={t('journeys.panels.sub45')}>
        {T("Ticket number", "Big, show-at-the-counter number.", "showNumber")}
        {T("Position in line", "‘N ahead of you’ with a live progress bar.", "showPosition")}
        {T("Estimated wait", "Minutes, recalculated as the line moves.", "showEta")}
        {T("Leave the queue", "Two-tap leave with confirmation — releases the spot.", "allowLeave")}
        {T("Offer an appointment instead", "‘Can't wait?’ hands off to your published appointment journey with service and branch pre-filled.", "offerAppointment")}
      </JPane>
      <JPane title={t('journeys.panels.title35')} sub={t('journeys.panels.sub46')}>
        <textarea className="textarea" rows={2} placeholder={t('journeys.panels.placeholder3')} value={c.note} onChange={(e) => set({ steps: { ticket: { note: e.target.value } } })} />
      </JPane>
    </React.Fragment>
  );
}

// ═══ Conversation (chat journeys) ═══
function JChatPanel({ j, set }: PanelProps) {
  const t = useT();
  const c = j.steps.chat;
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title41')} sub={t('journeys.panels.sub47')}>
        <Field label={t('journeys.panels.label56')}><Input value={c.assistantName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ steps: { chat: { assistantName: e.target.value } } })} style={{ maxWidth: 280 }} /></Field>
        <Field label={t('journeys.panels.label57')}><textarea className="textarea" rows={3} value={c.greeting} onChange={(e) => set({ steps: { chat: { greeting: e.target.value } } })} /></Field>
        <Field label={t('journeys.panels.label58')}>
          <JOptionCards value={c.tone} cols={3} onChange={(v) => set({ steps: { chat: { tone: v } } })} options={[
            { id: "concierge", label: "Concierge", desc: "Warm, unhurried, service-first." },
            { id: "coach", label: "Coach", desc: "Upbeat and knowledgeable — talks shop." },
            { id: "efficient", label: "Efficient", desc: "Short, direct, fastest path to booked." },
          ]} />
        </Field>
      </JPane>
      <JPane title={t('journeys.panels.title42')}>
        <OpsToggleRow label={t('journeys.panels.label59')} sub={t('journeys.panels.sub48')} value={c.suggestions !== false} onChange={(v) => set({ steps: { chat: { suggestions: v } } })} />
        <OpsToggleRow label={t('journeys.panels.label60')} sub={t('journeys.panels.sub49')} value={j.steps.details.consent.required} onChange={(v) => set({ steps: { details: { consent: { required: v } } } })} />
        <div style={{ padding: "10px 12px", borderRadius: "var(--jrni-radius-sm)", background: "var(--jrni-color-primary-4)", border: "1px solid var(--jrni-color-primary-3)", fontSize: 11.5, color: "var(--jrni-color-text-body)", lineHeight: 1.55, display: "flex", gap: 8 }}>
          <I.Sparkle size={13} style={{ color: "var(--jrni-color-primary-1)", flexShrink: 0, marginTop: 1 }} />
          <span>{t('journeys.panels.text35')}</span>
        </div>
      </JPane>
    </React.Fragment>
  );
}

// ═══ Events (hub journeys) ═══
function JEventsPanel({ j, set }: PanelProps) {
  const t = useT();
  const ev = (j.custom && j.custom.events) || [];
  const locs = (j.custom && j.custom.locations) || (j.custom && j.custom.locations || []);
  const e2 = j.steps.events || {};
  const isE = j.type === "event";
  const staffList = (j.custom && j.custom.staff) || [];
  const upd = (i: number, patch: Record<string, unknown>) => set({ custom: { ...j.custom, events: ev.map((x: any, xi: number) => xi === i ? { ...x, ...patch } : x) } });
  const th = resolveTheme(j);
  const imgs = e2.images || {};
  const fileRef = React.useRef<HTMLInputElement | null>(null);
  const pendingId = React.useRef<string | null>(null);
  const setImg = (evId: string, val: any) => set({ steps: { events: { images: { [evId]: val } } } });
  const pickFile = (evId: string) => { pendingId.current = evId; fileRef.current && fileRef.current.click(); };
  const onFile = (x: React.ChangeEvent<HTMLInputElement>) => {
    const f = x.target.files && x.target.files[0];
    x.target.value = "";
    const evId = pendingId.current;
    if (!f || !evId) return;
    const rd = new FileReader();
    rd.onload = () => {
      const im = new Image();
      im.onload = () => {
        const w = 480, h = Math.round((im.height / im.width) * 480) || 300;
        const cv = document.createElement("canvas"); cv.width = w; cv.height = h;
        cv.getContext("2d")?.drawImage(im, 0, 0, w, h);
        setImg(evId, { kind: "upload", src: cv.toDataURL("image/jpeg", 0.78) });
        toast({ title: "Image added", sub: f.name + " → shown on the event card" });
      };
      im.src = String(rd.result || "");
    };
    rd.readAsDataURL(f);
  };
  const evPreview = (evId: string) => eventImage({ ...j, steps: { ...j.steps, events: { ...e2, showImages: true } } }, th, evId);
  return (
    <React.Fragment>
      {isE && (
        <JPane title={t('journeys.panels.title43')} sub={t('journeys.panels.sub50')}>
          <Field label={t('journeys.panels.label61')}>
            <SegmentedControl value={e2.display || "list"} onChange={(v) => set({ steps: { events: { display: v } } })} options={[{ value: "list", label: "List" }, { value: "tiles", label: "Tiles" }, { value: "showcase", label: "Showcase" }]} />
          </Field>
          <OpsToggleRow label={t('journeys.panels.label62')} sub={t('journeys.panels.sub51')} value={e2.showLocation !== false} onChange={(v) => set({ steps: { events: { showLocation: v } } })} />
          <OpsToggleRow label={t('journeys.panels.label63')} sub={t('journeys.panels.sub52')} value={e2.showCategories !== false} onChange={(v) => set({ steps: { events: { showCategories: v } } })} />
          <OpsToggleRow label={t('journeys.panels.label64')} value={e2.showHost !== false} onChange={(v) => set({ steps: { events: { showHost: v } } })} />
          <OpsToggleRow label={t('journeys.panels.label65')} sub={t('journeys.panels.sub53')} value={e2.capacityBar !== false} onChange={(v) => set({ steps: { events: { capacityBar: v } } })} />
        </JPane>
      )}
      {!isE && (
        <JPane title={t('journeys.panels.title44')} sub={t('journeys.panels.sub54')}>
          <SegmentedControl value={e2.display || "list"} onChange={(v) => set({ steps: { events: { display: v } } })} options={[{ value: "list", label: "List" }, { value: "tiles", label: "Tiles" }, { value: "showcase", label: "Showcase" }]} />
        </JPane>
      )}
      <JPane title={isE ? "Your events" : "Events at your locations"} sub={isE ? "The catalog customers browse — chronological order works best." : "Shown on the location hub — customers reserve a place in the same flow."}
             right={<Btn size="sm" variant="secondary" onClick={() => set({ custom: { ...j.custom, events: [...ev, isE ? { id: "ev" + Date.now(), title: "", date: "", time: "", end: "", locId: locs[0] && locs[0].id, room: "", hostId: staffList[0] && staffList[0].id, category: "", capacity: 0, spots: 0, desc: "" } : { id: "ev" + Date.now(), title: "", date: "", time: "", locId: locs[0] && locs[0].id, spots: 0, desc: "" }] } })} icon={<I.Plus size={12} />}>{t('journeys.panels.text36')}</Btn>}>
        {!isE && <OpsToggleRow label={t('journeys.panels.label66')} value={e2.enabled !== false} onChange={(v) => set({ steps: { events: { enabled: v } } })} />}
        <OpsToggleRow label={t('journeys.panels.label67')} value={e2.showSpots !== false} onChange={(v) => set({ steps: { events: { showSpots: v } } })} />
        <OpsToggleRow label={t('journeys.panels.label68')} sub={t('journeys.panels.sub55')} value={e2.showImages !== false} onChange={(v) => set({ steps: { events: { showImages: v } } })} />
        {/* ui-raw: hidden native file picker triggered via ref; Input models no file type and would render visible chrome */}
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} />
        <div className="col" style={{ gap: 10 }}>
          {ev.map((e: any, i: number) => (
            <div key={e.id} className="col" style={{ gap: 8, padding: "10px 12px", background: "var(--jrni-color-surface-card-soft)", border: "1px solid var(--jrni-color-neutral-5)", borderRadius: "var(--jrni-radius-sm)" }}>
              <div className="row" style={{ gap: 8 }}>
                <Input size="sm" value={e.title} onChange={(x: React.ChangeEvent<HTMLInputElement>) => upd(i, { title: x.target.value })} style={{ flex: 1 }} />
                <Btn variant="ghost" onClick={() => set({ custom: { ...j.custom, events: ev.filter((_: any, xi: number) => xi !== i) } })} title={t('journeys.panels.title31')} style={{ width: 28, height: 28, padding: 0, border: "1px solid var(--jrni-color-neutral-5)", background: T.white, borderRadius: 4, color: T.red1, flexShrink: 0 }} iconOnly={<I.X size={11} />} />
              </div>
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                <Input size="sm" value={e.date} onChange={(x: React.ChangeEvent<HTMLInputElement>) => upd(i, { date: x.target.value })} style={{ width: 100 }} title={t('journeys.panels.title45')} />
                <Input size="sm" value={e.time} onChange={(x: React.ChangeEvent<HTMLInputElement>) => upd(i, { time: x.target.value })} style={{ width: 86 }} title={t('journeys.panels.title46')} />
                {isE && <Input size="sm" value={e.end || ""} placeholder={t('journeys.panels.placeholder4')} onChange={(x: React.ChangeEvent<HTMLInputElement>) => upd(i, { end: x.target.value })} style={{ width: 82 }} title={t('journeys.panels.title47')} />}
                <Dropdown value={e.locId ?? null} onChange={(v) => upd(i, { locId: v })} style={{ flex: 1, minWidth: 140 }} options={locs.map((l: any) => ({ value: l.id, label: l.name }))} />
                {!isE && <NumberField value={e.spots} onChange={(n) => upd(i, { spots: n })} style={{ width: 68, height: 30 }} ariaLabel={t('journeys.panels.title48')} />}
              </div>
              {isE && (
                <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <Input size="sm" value={e.room || ""} placeholder={t('journeys.panels.placeholder5')} onChange={(x: React.ChangeEvent<HTMLInputElement>) => upd(i, { room: x.target.value })} style={{ width: 108 }} title={t('journeys.panels.title49')} />
                  <Input size="sm" value={e.category || ""} placeholder={t('journeys.panels.placeholder6')} onChange={(x: React.ChangeEvent<HTMLInputElement>) => upd(i, { category: x.target.value })} style={{ width: 104 }} title={t('journeys.panels.title50')} />
                  <Dropdown value={e.hostId || ""} onChange={(v) => upd(i, { hostId: v || undefined })} style={{ flex: 1, minWidth: 120 }} aria-label={t('journeys.panels.title51')} options={[
                    { value: "", label: t('journeys.panels.text37') },
                    ...staffList.map((s: any) => ({ value: s.id, label: s.name })),
                  ]} />
                  <label className="row" style={{ gap: 5, alignItems: "center", fontSize: 10, color: "var(--jrni-color-text-soft)", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em" }} title={t('journeys.panels.title52')}>{t('journeys.panels.text38')}<NumberField value={e.capacity || 0} onChange={(n) => upd(i, { capacity: n })} style={{ width: 60, height: 30 }} /></label>
                  <label className="row" style={{ gap: 5, alignItems: "center", fontSize: 10, color: "var(--jrni-color-text-soft)", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em" }} title={t('journeys.panels.title53')}>{t('journeys.panels.text39')}<NumberField value={e.spots} onChange={(n) => upd(i, { spots: n })} style={{ width: 60, height: 30 }} /></label>
                </div>
              )}
              <Input size="sm" placeholder={t('journeys.panels.placeholder7')} value={e.desc} onChange={(x: React.ChangeEvent<HTMLInputElement>) => upd(i, { desc: x.target.value })} />
              <div className="row" style={{ gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                {(() => { const pv = evPreview(e.id); return <span style={{ width: 56, height: 38, borderRadius: 7, flexShrink: 0, border: "1px solid var(--jrni-color-neutral-5)", ...(pv || { background: "var(--jrni-color-neutral-6)" }), display: "grid", placeItems: "center" }}>{!pv && <I.Calendar size={13} style={{ color: "var(--jrni-color-text-soft)" }} />}</span>; })()}
                <span style={{ fontSize: 11, color: "var(--jrni-color-text-soft)", fontWeight: 600 }}>{t('journeys.panels.text40')}</span>
                <span className="row" style={{ gap: 5, alignItems: "center", flexWrap: "wrap" }}>
                  {TILE_PRESETS.map((pr) => {
                    const cur = imgs[e.id];
                    const active = cur && cur.kind === "preset" && cur.id === pr.id;
                    // ui-raw: 22px color-preset swatch button (gradient/brand fill, active ring) — a visual picker cell, not a labeled Btn
                    return <button key={pr.id} title={t(pr.labelKey, pr.label)} onClick={() => setImg(e.id, { kind: "preset", id: pr.id })} style={{ width: 22, height: 22, borderRadius: 6, cursor: "pointer", padding: 0, background: pr.css || `linear-gradient(135deg, ${th.brandColor}, ${th.accentColor})`, border: active ? "2px solid var(--jrni-color-primary-1)" : "1px solid var(--jrni-color-neutral-4)", boxShadow: active ? "0 0 0 2px var(--jrni-color-primary-3)" : "none" }} />;
                  })}
                  <Btn size="sm" variant="secondary" onClick={() => pickFile(e.id)} title={t('journeys.panels.title20')} icon={<Icon name="edit" size={12} />}>{imgs[e.id] && imgs[e.id].kind === "upload" ? t('journeys.panels.text18') : t('journeys.panels.text19')}</Btn>
                  {imgs[e.id] && <Btn size="sm" variant="ghost" title={t('journeys.panels.title21')} onClick={() => setImg(e.id, undefined)} iconOnly={<I.X size={11} />} />}
                </span>
              </div>
            </div>
          ))}
          {!ev.length && <span className="field-help">{isE ? t('journeys.panels.text41') : t('journeys.panels.text42')}</span>}
        </div>
      </JPane>
      {isE && (
        <JPane title={t('journeys.panels.title54')} sub={t('journeys.panels.sub56')}>
          <OpsToggleRow label={t('journeys.panels.label69')} sub={t('journeys.panels.sub57')} value={e2.waitlist !== false} onChange={(v) => set({ steps: { events: { waitlist: v } } })} />
          <span className="field-help">{t('journeys.panels.text43')}{" "}<b>{t('journeys.panels.text44')}</b>.</span>
        </JPane>
      )}
      <JPane title={t('journeys.panels.title55')} sub={t('journeys.panels.sub58')}>
        <textarea className="textarea" rows={2} value={e2.rsvpNote || ""} onChange={(x) => set({ steps: { events: { rsvpNote: x.target.value } } })} placeholder={t('journeys.panels.placeholder8')} />
      </JPane>
    </React.Fragment>
  );
}

// ═══ On-screen text ═══
function JTextPanel({ j, set }: PanelProps) {
  const t = useT();
  const isQ = j.type === "queue", isE = j.type === "event";
  const HIDE = isQ ? { Staff: 1, Time: 1, Confirmation: 1 } : isE ? { Services: 1, Locations: 1, Staff: 1, Time: 1, Confirmation: 1, "Queue ticket": 1 } : { "Queue ticket": 1 };
  const K = TEXT_KEYS.filter((k) => !(HIDE as Record<string, number>)[k.group] && (!k.types || k.types.includes(j.type || "appointment")));
  const groups = [...new Set(K.map((k) => k.group))];
  return (
    <React.Fragment>
      {groups.map((g) => (
        <JPane key={g} title={t(K.find((k) => k.group === g)!.groupKey, g)} sub={g === "Welcome" ? "Used by the Hero header style." : undefined}>
          {K.filter((k) => k.group === g).map((k) => {
            const val = j.text[k.key];
            const customized = val != null && val !== k.def;
            return (
              <Field key={k.key} label={t(k.labelKey, k.label)}>
                <div className="row" style={{ gap: 6, alignItems: "center" }}>
                  <Input value={val != null ? val : k.def} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ text: { [k.key]: e.target.value } })} style={{ flex: 1 }} />
                  {customized && <Btn size="sm" variant="ghost" title={t('journeys.panels.title56')} onClick={() => set({ text: { [k.key]: undefined } })} iconOnly={<I.Undo size={12} />} />}
                </div>
                {customized && <span className="field-help" style={{ color: "var(--jrni-color-primary-1)" }}>{t('journeys.panels.text45')}{k.def.slice(0, 48)}{k.def.length > 48 ? "…" : ""}”</span>}
              </Field>
            );
          })}
        </JPane>
      ))}
    </React.Fragment>
  );
}

// ═══ Share & embed ═══
function JCodeBlock({ code }: { code: string }) {
  const t = useT();
  return (
    <div style={{ position: "relative" }}>
      {/* ui-raw: fixed dark code-block theme (syntax preview) — dark surface + light-blue mono text stay put in both app themes, so no T.* token applies */}
      <pre style={{ margin: 0, padding: "12px 14px", background: "#14161E", color: "#D6E4F0", borderRadius: "var(--jrni-radius-sm)", fontSize: 11, lineHeight: 1.6, overflowX: "auto", fontFamily: "var(--jrni-font-family-mono)", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{code}</pre>
      <Btn size="sm" style={{ position: "absolute", top: 8, right: 8, background: "rgba(255,255,255,0.12)", color: T.onColor, borderColor: "rgba(255,255,255,0.2)" }} onClick={() => toast({ title: "Snippet copied" })}>{t('journeys.panels.text46')}</Btn>
    </div>
  );
}

function JSharePanel({ j, set, onPreview }: SharePanelProps) {
  const t = useT();
  const url = journeyURL(j);
  const [widget, setWidget] = React.useState({ label: t("journeys.panels.widgetDefaultLabel", "Book an appointment"), position: "bottom-right", open: false });
  const th = resolveTheme(j);
  const live = j.status === "published";
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title57')} sub={live ? "Live — share it in campaigns, email signatures, social bios and QR codes." : "Goes live when you publish. Share it anywhere once it does."}>
        <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span className="mono" style={{ flex: 1, minWidth: 200, padding: "10px 12px", background: "var(--jrni-color-surface-card-soft)", border: "1px solid var(--jrni-color-neutral-5)", borderRadius: "var(--jrni-radius-sm)", fontSize: 12.5, color: live ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-soft)" }}>{url}</span>
          <Btn size="sm" variant="secondary" onClick={() => { try { navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ } toast({ title: "Link copied", sub: url }); }} icon={<I.Edit size={12} />}>{t('journeys.panels.text46')}</Btn>
          {/* "View live site" read a LIVE_SITES map absent from this port (`({})[j.id]`
              — permanently undefined, so it never rendered). Removed rather than typed:
              a real live-site URL needs a declared op (REQ-063). */}
          <Btn size="sm" variant="secondary" onClick={onPreview} icon={<I.Browser size={12} />}>{t('journeys.panels.text47')}</Btn>
        </div>
        {!live && <span className="field-help" style={{ color: T.orange1 }}>{t('journeys.panels.text48')}</span>}
      </JPane>
      <JPane title={t('journeys.panels.title58')} sub={t('journeys.panels.sub59')}>
        {/* Docket 2026-07-26 §1.1: neutral accessible iframe title via the
            catalog — no per-journey tenant-name field exists on this data
            (journeyURL only derives from domain/slug), so no interpolation. */}
        <JCodeBlock code={`<iframe src="${url}?embed=1"\n  width="100%" height="720"\n  style="border:0;border-radius:12px"\n  title="${t("journeys.panels.embedIframeTitle", "Book an appointment")}"></iframe>`} />
      </JPane>
      <JPane title={t('journeys.panels.title59')} sub={t('journeys.panels.sub60')}>
        <div className="row gap-3" style={{ flexWrap: "wrap" }}>
          <Field label={t('journeys.panels.label70')} flex><Input value={widget.label} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWidget({ ...widget, label: e.target.value })} /></Field>
          <Field label={t('journeys.panels.label71')} flex>
            <SegmentedControl value={widget.position} onChange={(v) => setWidget({ ...widget, position: v })} options={[{ value: "bottom-right", label: "Bottom right" }, { value: "bottom-left", label: "Bottom left" }]} />
          </Field>
        </div>
        <JCodeBlock code={`<script src="https://${baseURL}/widget.js"\n  data-journey="${j.slug}"\n  data-label="${widget.label}"\n  data-position="${widget.position}"><\/script>`} />
        <Field label={t('journeys.panels.label72')}>
          <div style={{ position: "relative", height: 220, borderRadius: "var(--jrni-radius-sm)", border: "1px solid var(--jrni-color-neutral-5)", background: "repeating-linear-gradient(0deg, var(--jrni-color-surface-card-soft), var(--jrni-color-surface-card-soft) 34px, var(--jrni-color-neutral-6) 34px, var(--jrni-color-neutral-6) 35px)", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 10, left: 12, right: 12, height: 10, borderRadius: 3, background: "var(--jrni-color-neutral-5)", width: "40%" }} />
            <div style={{ position: "absolute", top: 28, left: 12, height: 7, borderRadius: 3, background: "var(--jrni-color-neutral-6)", width: "62%", border: "1px solid var(--jrni-color-neutral-5)" }} />
            <span style={{ position: "absolute", top: 8, right: 12, fontSize: 9.5, color: "var(--jrni-color-text-soft)" }}>{t('journeys.panels.text49')}</span>
            {widget.open && (
              <div style={{ position: "absolute", bottom: 58, [widget.position === "bottom-right" ? "right" : "left"]: 12, width: 230, background: T.white, borderRadius: 12, boxShadow: "0 12px 32px rgba(20,22,30,0.22)", border: "1px solid var(--jrni-color-neutral-5)", overflow: "hidden" }}>
                <div style={{ padding: "10px 12px", background: th.brandColor, color: T.onColor, fontSize: 11.5, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>{j.name}<I.X size={11} style={{ cursor: "pointer" }} /></div>
                <div style={{ padding: 12, fontSize: 10.5, color: "var(--jrni-color-text-body)", lineHeight: 1.5 }}>{t('journeys.panels.text50')}</div>
              </div>
            )}
            {/* ui-raw: simulated embed-widget launcher rendered inside the live preview, styled with the tenant's own brand color/radius — not the admin app's Btn */}
            <button onClick={() => setWidget({ ...widget, open: !widget.open })} style={{ position: "absolute", bottom: 12, [widget.position === "bottom-right" ? "right" : "left"]: 12, padding: "10px 16px", background: th.brandColor, color: T.onColor, border: 0, borderRadius: th.radius === 0 ? 4 : 999, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", boxShadow: "0 6px 16px rgba(20,22,30,0.25)", display: "flex", gap: 7, alignItems: "center" }}><I.Calendar size={13} /> {widget.label}</button>
          </div>
        </Field>
      </JPane>
    </React.Fragment>
  );
}

// ═══ Hub layout (hub journeys) ═══
function JHubPanel({ j, set }: PanelProps) {
  const t = useT();
  const h = j.steps.hub || {};
  const layout = h.layout || "stacked";
  const label = (key: string, def: string) => (j.text && j.text[key] != null) ? j.text[key] : def;
  const setLabel = (key: string, v: string) => set({ text: { [key]: v } });
  const StackWire = (
    <span style={{ display: "flex", flexDirection: "column", gap: 4, width: 46 }}>
      {[0, 1, 2].map((i) => <span key={i} style={{ height: 9, borderRadius: 3, background: i === 0 ? "var(--jrni-color-primary-1)" : "var(--jrni-color-neutral-4)" }} />)}
    </span>
  );
  const ColsWire = (
    <span style={{ display: "flex", gap: 4, width: 46 }}>
      {[0, 1, 2].map((i) => <span key={i} style={{ flex: 1, height: 30, borderRadius: 3, background: i === 0 ? "var(--jrni-color-primary-1)" : "var(--jrni-color-neutral-4)" }} />)}
    </span>
  );
  return (
    <React.Fragment>
      <JPane title={t('journeys.panels.title60')} sub={t('journeys.panels.sub61')}>
        <JOptionCards value={layout} onChange={(v) => set({ steps: { hub: { layout: v } } })} options={[
          { id: "stacked", label: "Stacked", desc: "One section after another — the classic single-column hub.", visual: StackWire },
          { id: "columns", label: "Side by side", desc: "Appointments, events and the line in parallel columns with headers.", visual: ColsWire },
        ]} />
      </JPane>
      <JPane title={t('journeys.panels.title61')} sub={t('journeys.panels.sub62')}>
        <OpsToggleRow label={t('journeys.panels.label73')} sub={t('journeys.panels.sub63')} value={h.showAppointments !== false} onChange={(v) => set({ steps: { hub: { showAppointments: v } } })} />
        <Field label={t('journeys.panels.label74')}><Input value={label("hub.appointments", "Private appointments")} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel("hub.appointments", e.target.value)} placeholder={t('journeys.panels.placeholder9')} style={{ maxWidth: 320 }} /></Field>
        <OpsToggleRow label={t('journeys.panels.label75')} sub={t('journeys.panels.sub64')} value={j.steps.events && j.steps.events.enabled !== false} onChange={(v) => set({ steps: { events: { enabled: v } } })} />
        <Field label={t('journeys.panels.label76')}><Input value={label("hub.events", "What's on")} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel("hub.events", e.target.value)} placeholder={t('journeys.panels.placeholder10')} style={{ maxWidth: 320 }} /></Field>
        <OpsToggleRow label={t('journeys.panels.label77')} sub={t('journeys.panels.sub65')} value={h.showQueue !== false} onChange={(v) => set({ steps: { hub: { showQueue: v } } })} />
        <Field label={t('journeys.panels.label78')}><Input value={label("hub.queue", "Join the line")} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel("hub.queue", e.target.value)} placeholder={t('journeys.panels.placeholder11')} style={{ maxWidth: 320 }} /></Field>
      </JPane>
    </React.Fragment>
  );
}

export { JPane, JOptionCards, JWire, BrandScanCard, applyScanToJourney, JServiceImagesPane, JQueuePanel, JTicketPanel, JChatPanel, JEventsPanel, JHubPanel, JDesignPanel, JHeaderFooterPanel, JFlowPanel, JServicesPanel, JLocationsPanel, JStaffPanel, JTimePanel, JDetailsPanel, JConfirmPanel, JTextPanel, JSharePanel, JCodeBlock };
